import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown, ArrowUp, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Clock, CornerDownRight, Download, FolderTree,
  Layers, List, MoreHorizontal, Network, Plus, Star, Trash2, Upload, Webhook,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getFlows, createFlow, deleteFlow, activateFlow, deactivateFlow, exportFlow, importFlow,
  addFavorite, removeFavorite,
} from '../api/flows';
import { getTags, bulkAddTag, bulkRemoveTag } from '../api/tags';
import { FlowSummary } from '../types';
import { Chip } from '../components/Chip';
import { TagPopover } from '../components/TagPopover';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { STATUS_DOT } from '../lib/executionStatus';
import { cn } from '../lib/utils';
import {
  buildCallTree, callersById, filterCallTree, groupByNamespace, NO_NAMESPACE, type CallTreeNode,
} from './flowsListGrouping';

type SortKey = 'name' | 'status' | 'trigger' | 'lastRun' | 'updated';
type ViewMode = 'list' | 'namespace' | 'tree';

const VIEW_KEY = 'flows-view';
const COLLAPSED_KEY = 'flows-collapsed';
const COLUMN_COUNT = 8;

const VIEW_MODES: { mode: ViewMode; label: string; icon: typeof List }[] = [
  { mode: 'list', label: 'List', icon: List },
  { mode: 'namespace', label: 'Namespaces', icon: Layers },
  { mode: 'tree', label: 'Call tree', icon: FolderTree },
];

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function store(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable — the view just forgets its state.
  }
}

function sortValue(flow: FlowSummary, key: SortKey): string | number {
  switch (key) {
    case 'name': return flow.name.toLowerCase();
    case 'status': return flow.isActive ? 0 : 1;
    case 'trigger': return flow.schedule ? 0 : flow.hasBatchConfig ? 1 : 2;
    case 'lastRun': return flow.lastExecutionAt ? -new Date(flow.lastExecutionAt).getTime() : Number.POSITIVE_INFINITY;
    case 'updated': return new Date(flow.updatedAt).getTime();
  }
}

function comparator(key: SortKey, dir: 'asc' | 'desc') {
  const sign = dir === 'asc' ? 1 : -1;
  return (a: FlowSummary, b: FlowSummary) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    let cmp = typeof av === 'string' && typeof bv === 'string'
      ? av.localeCompare(bv)
      : av < bv ? -1 : av > bv ? 1 : 0;
    if (cmp === 0) cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    return cmp * sign;
  };
}

function formatAgo(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

function SortIndicator({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return null;
  return dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
}

export function FlowsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [newName, setNewName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPopoverOpen, setBulkPopoverOpen] = useState(false);
  const [bulkPopoverAnchor, setBulkPopoverAnchor] = useState<HTMLElement | null>(null);
  const [rowPopoverFlowId, setRowPopoverFlowId] = useState<string | null>(null);
  const [rowPopoverAnchor, setRowPopoverAnchor] = useState<HTMLElement | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [view, setView] = useState<ViewMode>(() => readStored<ViewMode>(VIEW_KEY, 'list'));
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(readStored<string[]>(COLLAPSED_KEY, [])));
  const importInputRef = useRef<HTMLInputElement>(null);

  function changeView(next: ViewMode) {
    setView(next);
    store(VIEW_KEY, next);
  }

  function setAllCollapsed(keys: string[], collapse: boolean) {
    setCollapsed(curr => {
      const next = new Set(curr);
      for (const key of keys) {
        if (collapse) next.add(key); else next.delete(key);
      }
      store(COLLAPSED_KEY, [...next]);
      return next;
    });
  }

  function toggleCollapsed(key: string) {
    setCollapsed(curr => {
      const next = new Set(curr);
      if (next.has(key)) next.delete(key); else next.add(key);
      store(COLLAPSED_KEY, [...next]);
      return next;
    });
  }

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const { data: flows = [], isLoading } = useQuery({
    queryKey: ['flows'],
    queryFn: getFlows,
  });

  const { data: allTags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: getTags,
    staleTime: 30_000,
  });
  const tagColorByName = useMemo(
    () => new Map(allTags.map(t => [t.name, t.color] as const)),
    [allTags],
  );

  const bulkAddMutation = useMutation({
    mutationFn: ({ flowIds, tagName }: { flowIds: string[]; tagName: string }) =>
      bulkAddTag({ flowIds, tagName }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
  const bulkRemoveMutation = useMutation({
    mutationFn: ({ flowIds, tagName }: { flowIds: string[]; tagName: string }) =>
      bulkRemoveTag({ flowIds, tagName }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: (flow: FlowSummary) => (flow.isFavorite ? removeFavorite(flow.id) : addFavorite(flow.id)),
    onMutate: async flow => {
      await qc.cancelQueries({ queryKey: ['flows'] });
      const previous = qc.getQueryData<FlowSummary[]>(['flows']);
      qc.setQueryData<FlowSummary[]>(['flows'], curr =>
        curr?.map(f => (f.id === flow.id ? { ...f, isFavorite: !flow.isFavorite } : f)));
      return { previous };
    },
    onError: (err: Error, _flow, context) => {
      if (context?.previous) qc.setQueryData(['flows'], context.previous);
      toast.error(`Favorite failed: ${err.message}`);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['flows'] }),
  });

  const nameById = useMemo(() => new Map(flows.map(f => [f.id, f.name] as const)), [flows]);
  const callers = useMemo(() => callersById(flows), [flows]);
  const flowLabel = (id: string) => nameById.get(id) ?? `(unknown: ${id.slice(0, 8)})`;

  const filtersActive = !!search.trim() || statusFilter !== 'all' || tagFilter.length > 0 || favoritesOnly;

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (f: FlowSummary) => {
      if (statusFilter === 'active' && !f.isActive) return false;
      if (statusFilter === 'inactive' && f.isActive) return false;
      if (favoritesOnly && !f.isFavorite) return false;
      if (tagFilter.length > 0 && !tagFilter.every(t => f.tags.includes(t))) return false;
      if (!needle) return true;
      if (f.name.toLowerCase().includes(needle)) return true;
      if (f.description && f.description.toLowerCase().includes(needle)) return true;
      if (f.webhookAlias && f.webhookAlias.toLowerCase().includes(needle)) return true;
      if (f.tags.some(t => t.toLowerCase().includes(needle))) return true;
      return false;
    };
  }, [search, statusFilter, tagFilter, favoritesOnly]);

  const compare = useMemo(() => comparator(sortKey, sortDir), [sortKey, sortDir]);
  const filteredFlows = useMemo(() => flows.filter(matches), [flows, matches]);
  const sortedFlows = useMemo(() => [...filteredFlows].sort(compare), [filteredFlows, compare]);

  function toggleTagFilter(tag: string) {
    setTagFilter(curr => curr.includes(tag) ? curr.filter(t => t !== tag) : [...curr, tag]);
  }

  const createMutation = useMutation({
    mutationFn: () => createFlow({
      name: newName,
      definition: JSON.stringify({ nodes: [], edges: [] }),
    }),
    onSuccess: flow => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      navigate(`/flows/${flow.id}`);
    },
    onError: (err: Error) => toast.error(`Create failed: ${err.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFlow,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      toast.success('Flow deleted');
    },
    onError: (err: Error) => toast.error(`Delete failed: ${err.message}`),
  });

  const toggleMutation = useMutation({
    mutationFn: (flow: FlowSummary) =>
      flow.isActive ? deactivateFlow(flow.id) : activateFlow(flow.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flows'] }),
    onError: (err: Error) => toast.error(`Status change failed: ${err.message}`),
  });

  const importMutation = useMutation({
    mutationFn: importFlow,
    onSuccess: flow => {
      qc.invalidateQueries({ queryKey: ['flows'] });
      navigate(`/flows/${flow.id}`);
    },
    onError: (err: Error) => toast.error(`Import failed: ${err.message}`),
  });

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => importMutation.mutate(reader.result as string);
    reader.readAsText(file);
    e.target.value = '';
  }

  function handleExport(flow: FlowSummary) {
    exportFlow(flow.id).then(data => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${flow.name.replace(/\s+/g, '-').toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;

  function renderRow(
    flow: FlowSummary,
    key: string,
    tree?: { depth: number; node: CallTreeNode },
  ) {
    const callsTo = flow.calledFlowIds.filter(id => id !== flow.id);
    const calledBy = callers.get(flow.id) ?? [];
    const hasChildren = !!tree && tree.node.children.length > 0;
    const treeCollapsed = !!tree && collapsed.has(`tree:${tree.node.path}`);

    return (
      <TableRow key={key} className="group">
        <TableCell className="w-8 pr-0">
          <input
            type="checkbox"
            checked={selectedIds.has(flow.id)}
            onChange={e => {
              setSelectedIds(curr => {
                const next = new Set(curr);
                if (e.target.checked) next.add(flow.id);
                else next.delete(flow.id);
                return next;
              });
            }}
            aria-label={`Select ${flow.name}`}
          />
        </TableCell>
        <TableCell className="min-w-[18rem] max-w-[28rem]">
          <div className="flex items-start gap-1.5" style={tree ? { paddingLeft: tree.depth * 20 } : undefined}>
            {tree && (
              hasChildren ? (
                <button
                  type="button"
                  onClick={() => toggleCollapsed(`tree:${tree.node.path}`)}
                  className="mt-0.5 rounded text-muted-foreground hover:text-foreground"
                  aria-label={treeCollapsed ? `Expand ${flow.name}` : `Collapse ${flow.name}`}
                >
                  {treeCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              ) : tree.depth > 0 ? (
                <CornerDownRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" />
              ) : (
                <span className="w-4 shrink-0" />
              )
            )}
            <button
              type="button"
              onClick={() => favoriteMutation.mutate(flow)}
              className={cn(
                'mt-0.5 shrink-0 rounded transition-colors',
                flow.isFavorite ? 'text-warning' : 'text-muted-foreground/40 hover:text-warning',
              )}
              aria-label={flow.isFavorite ? `Unpin ${flow.name}` : `Pin ${flow.name}`}
              aria-pressed={flow.isFavorite}
              title={flow.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Star className="h-4 w-4" fill={flow.isFavorite ? 'currentColor' : 'none'} />
            </button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <Link to={`/flows/${flow.id}`} className="font-medium text-foreground hover:text-primary hover:underline">
                  {flow.name}
                </Link>
                {tree && tree.node.otherCallerIds.length > 0 && (
                  <span
                    className="rounded bg-muted px-1.5 text-[11px] text-muted-foreground"
                    title={`Also called by ${tree.node.otherCallerIds.map(flowLabel).join(', ')}`}
                  >
                    +{tree.node.otherCallerIds.length} caller{tree.node.otherCallerIds.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              {flow.description && (
                <p className="text-xs text-muted-foreground line-clamp-1" title={flow.description}>
                  {flow.description}
                </p>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-1">
                {flow.tags.map(t => (
                  <Chip
                    key={t}
                    label={t}
                    color={tagColorByName.get(t)}
                    onClick={() => {
                      if (!tagFilter.includes(t)) setTagFilter([...tagFilter, t]);
                    }}
                    onRemove={() => bulkRemoveMutation.mutate({ flowIds: [flow.id], tagName: t })}
                  />
                ))}
                <div className="relative">
                  <button
                    type="button"
                    onClick={e => {
                      setRowPopoverAnchor(e.currentTarget);
                      setRowPopoverFlowId(p => p === flow.id ? null : flow.id);
                    }}
                    className="rounded border border-dashed border-border px-1 text-xs text-muted-foreground opacity-0 transition-opacity hover:border-primary/60 hover:text-primary focus:opacity-100 group-hover:opacity-100"
                    title="Add tag"
                  >
                    + tag
                  </button>
                  {rowPopoverFlowId === flow.id && (
                    <TagPopover
                      allTags={allTags}
                      anchorEl={rowPopoverAnchor}
                      excludeNames={flow.tags}
                      onSelect={name => {
                        bulkAddMutation.mutate({ flowIds: [flow.id], tagName: name });
                        setRowPopoverFlowId(null);
                      }}
                      onClose={() => setRowPopoverFlowId(null)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex flex-col items-start gap-1">
            <Badge variant={flow.isActive ? 'success' : 'secondary'}>
              {flow.isActive ? 'Active' : 'Inactive'}
            </Badge>
            {flow.publishedRevisionNumber === null ? (
              <Badge variant="secondary" title="Nothing of this flow runs yet.">
                Never published
              </Badge>
            ) : flow.hasUnpublishedChanges ? (
              <Badge
                variant="warning"
                title={`Live is revision #${flow.publishedRevisionNumber}. The draft has changes that are not running.`}
              >
                Draft ahead of live
              </Badge>
            ) : null}
            {flow.failedSincePublish > 0 && (
              <span
                className="text-xs text-destructive"
                title={`Since publishing revision #${flow.publishedRevisionNumber}`}
              >
                {flow.failedSincePublish}/{flow.executionsSincePublish} failed since publish
              </span>
            )}
          </div>
        </TableCell>
        <TableCell className="whitespace-nowrap text-[13px]">
          {flow.schedule ? (
            <span
              className="inline-flex items-center gap-1.5"
              title={flow.nextRunAt ? `Next run: ${new Date(flow.nextRunAt).toLocaleString()}` : undefined}
            >
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-mono text-xs">{flow.schedule}</span>
              <Badge variant={flow.scheduleEnabled ? 'success' : 'secondary'}>
                {flow.scheduleEnabled ? 'on' : 'off'}
              </Badge>
            </span>
          ) : flow.hasBatchConfig ? (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Layers className="h-3.5 w-3.5" />
              Batch
            </span>
          ) : calledBy.length > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <CornerDownRight className="h-3.5 w-3.5" />
              Sub-flow
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Webhook className="h-3.5 w-3.5" />
              Webhook
            </span>
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap text-[13px]">
          {flow.lastExecutionAt && flow.lastExecutionId ? (
            <Link
              to={`/executions/${flow.lastExecutionId}`}
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
              title={`${flow.lastExecutionStatus} · ${new Date(flow.lastExecutionAt).toLocaleString()}`}
            >
              <span className={cn('h-2 w-2 rounded-full', STATUS_DOT[flow.lastExecutionStatus ?? ''] ?? 'bg-muted-foreground/30')} />
              {formatAgo(flow.lastExecutionAt)}
            </Link>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {callsTo.length === 0 && calledBy.length === 0 ? (
            <span>—</span>
          ) : (
            <div className="space-y-0.5">
              {callsTo.length > 0 && (
                <RelationLine prefix="Calls" ids={callsTo} label={flowLabel} />
              )}
              {calledBy.length > 0 && (
                <RelationLine prefix="Called by" ids={calledBy} label={flowLabel} />
              )}
            </div>
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap text-[13px] text-muted-foreground">
          {new Date(flow.updatedAt).toLocaleDateString()}
        </TableCell>
        <TableCell>
          <div className="flex items-center justify-end gap-1">
            <Button variant="outline" size="xs" onClick={() => toggleMutation.mutate(flow)}>
              {flow.isActive ? 'Deactivate' : 'Activate'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={`More actions for ${flow.name}`}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleExport(flow)}>
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => {
                    if (window.confirm(`Delete "${flow.name}"?`)) deleteMutation.mutate(flow.id);
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  function renderGroupHeader(key: string, label: React.ReactNode, count: number, icon?: React.ReactNode) {
    const isCollapsed = collapsed.has(key);
    return (
      <TableRow key={`group:${key}`} className="bg-muted/40 hover:bg-muted/60">
        <TableCell colSpan={COLUMN_COUNT} className="py-1.5">
          <button
            type="button"
            onClick={() => toggleCollapsed(key)}
            className="flex w-full items-center gap-2 text-left text-[13px] font-medium"
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            {icon}
            {label}
            <span className="font-normal text-muted-foreground">{count}</span>
          </button>
        </TableCell>
      </TableRow>
    );
  }

  function renderTree(nodes: CallTreeNode[], depth: number): React.ReactNode[] {
    return nodes.flatMap(node => [
      renderRow(node.flow, `tree:${node.path}`, { depth, node }),
      ...(collapsed.has(`tree:${node.path}`) ? [] : renderTree(node.children, depth + 1)),
    ]);
  }

  const callTree = view === 'tree' ? buildCallTree(flows, compare) : [];
  const namespaceGroups = view === 'namespace' ? groupByNamespace(sortedFlows) : [];
  const namespaceKey = (ns: string) => `group:ns:${ns.toLowerCase()}`;
  const treeKeys = (nodes: CallTreeNode[]): string[] =>
    nodes.flatMap(n => (n.children.length > 0 ? [`tree:${n.path}`, ...treeKeys(n.children)] : []));
  const collapsibleKeys = view === 'tree'
    ? treeKeys(callTree)
    : view === 'namespace'
      ? ['group:favorites', ...namespaceGroups.map(g => namespaceKey(g.key))]
      : [];
  const allCollapsed = collapsibleKeys.length > 0 && collapsibleKeys.every(k => collapsed.has(k));

  function renderBody(): React.ReactNode[] {
    if (view === 'tree') {
      return renderTree(filtersActive ? filterCallTree(callTree, matches) : callTree, 0);
    }

    if (view === 'namespace') {
      const favorites = sortedFlows.filter(f => f.isFavorite);
      const rows: React.ReactNode[] = [];
      if (favorites.length > 0 && !favoritesOnly) {
        rows.push(renderGroupHeader('group:favorites', 'Favorites', favorites.length,
          <Star className="h-3.5 w-3.5 text-warning" fill="currentColor" />));
        if (!collapsed.has('group:favorites')) rows.push(...favorites.map(f => renderRow(f, `fav:${f.id}`)));
      }
      for (const group of namespaceGroups) {
        const key = namespaceKey(group.key);
        rows.push(renderGroupHeader(
          key,
          group.key === NO_NAMESPACE ? <span className="text-muted-foreground">No namespace</span> : group.key,
          group.flows.length,
        ));
        if (!collapsed.has(key)) rows.push(...group.flows.map(f => renderRow(f, f.id)));
      }
      return rows;
    }

    const favoritesFirst = [...sortedFlows].sort((a, b) => Number(b.isFavorite) - Number(a.isFavorite));
    return favoritesFirst.map(f => renderRow(f, f.id));
  }

  const body = renderBody();

  return (
    <div>
      <PageHeader
        title="Flows"
        description="Build, publish and monitor your automations."
        actions={
          <>
            <input
              ref={importInputRef}
              type="file"
              accept=".json,.yaml,.yml"
              className="hidden"
              onChange={handleImportFile}
            />
            <Button
              variant="outline"
              onClick={() => importInputRef.current?.click()}
              disabled={importMutation.isPending}
            >
              <Upload className="h-4 w-4" />
              {importMutation.isPending ? 'Importing…' : 'Import'}
            </Button>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" />
              New flow
            </Button>
          </>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-input bg-card p-0.5 shadow-sm" role="group" aria-label="View">
          {VIEW_MODES.map(({ mode, label, icon: Icon }) => (
            <button
              key={mode}
              type="button"
              onClick={() => changeView(mode)}
              aria-pressed={view === mode}
              className={cn(
                'inline-flex h-7 items-center gap-1.5 rounded px-2.5 text-[13px] transition-colors',
                view === mode ? 'bg-primary/10 font-medium text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
        {collapsibleKeys.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAllCollapsed(collapsibleKeys, !allCollapsed)}
            title={view === 'tree'
              ? (allCollapsed ? 'Show every sub-flow' : 'Show only the top-level flows')
              : (allCollapsed ? 'Open every group' : 'Close every group')}
          >
            {allCollapsed ? <ChevronsUpDown className="h-3.5 w-3.5" /> : <ChevronsDownUp className="h-3.5 w-3.5" />}
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </Button>
        )}
        <Input
          type="text"
          placeholder="Search name, description, tag…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="h-8 w-64"
        />
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as 'all' | 'active' | 'inactive')}>
          <SelectTrigger className="h-8 w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          aria-pressed={favoritesOnly}
          onClick={() => setFavoritesOnly(f => !f)}
          className={cn(favoritesOnly && 'border-warning/50 bg-warning/10 text-warning hover:bg-warning/15 hover:text-warning')}
        >
          <Star className="h-3.5 w-3.5" fill={favoritesOnly ? 'currentColor' : 'none'} />
          Favorites
        </Button>
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setTagFilterOpen(o => !o)}
          >
            Tags {tagFilter.length > 0 ? `(${tagFilter.length})` : ''}
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
          {tagFilterOpen && (
            <div
              className="absolute top-full left-0 mt-1 z-10 bg-popover text-popover-foreground border border-border rounded-lg shadow-lg w-56 max-h-64 overflow-y-auto p-1"
              onMouseLeave={() => setTagFilterOpen(false)}
            >
              {allTags.length === 0 ? (
                <p className="text-xs text-muted-foreground px-2 py-1">No tags yet</p>
              ) : (
                allTags.map(t => (
                  <label
                    key={t.id}
                    className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-accent hover:text-accent-foreground cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={tagFilter.includes(t.name)}
                      onChange={() => toggleTagFilter(t.name)}
                    />
                    <Chip label={t.name} color={t.color} />
                    <span className="text-xs text-muted-foreground ml-auto">{t.flowCount}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>
        {tagFilter.map(t => (
          <Chip key={t} label={t} color={tagColorByName.get(t)} onRemove={() => toggleTagFilter(t)} />
        ))}
        {filtersActive && (
          <Button
            variant="ghost"
            size="xs"
            onClick={() => { setSearch(''); setStatusFilter('all'); setTagFilter([]); setFavoritesOnly(false); }}
          >
            Clear
          </Button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          {filteredFlows.length} of {flows.length}
        </span>
      </div>

      {selectedIds.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <span>{selectedIds.size} selected</span>
          <div className="relative">
            <Button
              variant="outline"
              size="xs"
              onClick={e => {
                setBulkPopoverAnchor(e.currentTarget);
                setBulkPopoverOpen(o => !o);
              }}
            >
              Add tag…
            </Button>
            {bulkPopoverOpen && (
              <TagPopover
                allTags={allTags}
                anchorEl={bulkPopoverAnchor}
                onSelect={name => {
                  bulkAddMutation.mutate({ flowIds: Array.from(selectedIds), tagName: name });
                  setBulkPopoverOpen(false);
                }}
                onClose={() => setBulkPopoverOpen(false)}
              />
            )}
          </div>
          <Button
            variant="ghost"
            size="xs"
            className="ml-auto"
            onClick={() => setSelectedIds(new Set())}
          >
            Deselect
          </Button>
        </div>
      )}

      <Dialog
        open={showCreate}
        onOpenChange={o => {
          setShowCreate(o);
          if (!o) setNewName('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create flow</DialogTitle>
            <DialogDescription>
              Give your flow a name. A prefix like <code className="font-mono">personio/</code> groups it
              with related flows.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="Flow name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && newName.trim() && !createMutation.isPending) createMutation.mutate();
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setNewName(''); }}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!newName.trim() || createMutation.isPending}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-8 pr-0">
              <input
                type="checkbox"
                checked={sortedFlows.length > 0 && sortedFlows.every(f => selectedIds.has(f.id))}
                onChange={e => {
                  if (e.target.checked) setSelectedIds(new Set(sortedFlows.map(f => f.id)));
                  else setSelectedIds(new Set());
                }}
                aria-label="Select all"
              />
            </TableHead>
            <SortableHead label="Name" sortKey="name" active={sortKey} dir={sortDir} onSort={handleSort} />
            <SortableHead label="Status" sortKey="status" active={sortKey} dir={sortDir} onSort={handleSort} />
            <SortableHead label="Trigger" sortKey="trigger" active={sortKey} dir={sortDir} onSort={handleSort} />
            <SortableHead label="Last run" sortKey="lastRun" active={sortKey} dir={sortDir} onSort={handleSort} />
            <TableHead>
              <span className="inline-flex items-center gap-1"><Network className="h-3 w-3" />Relations</span>
            </TableHead>
            <SortableHead label="Updated" sortKey="updated" active={sortKey} dir={sortDir} onSort={handleSort} />
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {body}
          {body.length === 0 && (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={COLUMN_COUNT} className="p-8 text-center text-muted-foreground">
                {flows.length === 0 ? 'No flows yet' : 'No flows match the current filter'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function SortableHead({
  label, sortKey, active, dir, onSort,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
}) {
  return (
    <TableHead className="p-0">
      <button
        onClick={() => onSort(sortKey)}
        className="flex h-9 w-full items-center gap-1 px-3 text-left hover:text-foreground"
      >
        {label}
        <SortIndicator active={active === sortKey} dir={dir} />
      </button>
    </TableHead>
  );
}

function RelationLine({ prefix, ids, label }: { prefix: string; ids: string[]; label: (id: string) => string }) {
  const shown = ids.slice(0, 2);
  const rest = ids.slice(2);
  return (
    <div className="line-clamp-2">
      {prefix}{' '}
      {shown.map((id, i) => (
        <span key={id}>
          {i > 0 && ', '}
          <Link to={`/flows/${id}`} className="text-primary hover:underline">{label(id)}</Link>
        </span>
      ))}
      {rest.length > 0 && <span title={rest.map(label).join(', ')}> +{rest.length}</span>}
    </div>
  );
}
